import { z } from 'zod';

export const AuditEventView = z.object({
  id: z.string().uuid(),
  seq: z.number().int(),
  occurredAt: z.iso.datetime(),
  actorType: z.enum(['user', 'service', 'system']),
  actorUserId: z.string().uuid().nullable(),
  actorClientId: z.string().nullable(),
  actorService: z.string().nullable(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  result: z.enum(['success', 'failure', 'denied']),
  correlationId: z.string().nullable(),
  executionId: z.string().uuid().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  hash: z.string(),
});
export type AuditEventView = z.infer<typeof AuditEventView>;

export const AuditList = z.object({
  items: z.array(AuditEventView),
  nextCursor: z.string().nullable(),
});
export type AuditList = z.infer<typeof AuditList>;

export const AuditVerification = z.object({
  chainKey: z.string(),
  valid: z.boolean(),
  eventsChecked: z.number().int(),
  headSeq: z.number().int(),
  headHash: z.string().nullable(),
  firstInvalidSeq: z.number().int().nullable(),
  reason: z.string().nullable(),
  verifiedAt: z.iso.datetime(),
});
export type AuditVerification = z.infer<typeof AuditVerification>;

export const CredentialKind = z.enum(['api_token', 'password', 'oauth_client_secret', 'generic']);

/** Metadata only. There is deliberately no field that could carry a secret value. */
export const CredentialView = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kind: CredentialKind,
  description: z.string(),
  status: z.enum(['active', 'revoked']),
  currentVersion: z.number().int(),
  fingerprint: z
    .string()
    .describe('Keyed fingerprint (HMAC) of the current value; not reversible.'),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  rotatedAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type CredentialView = z.infer<typeof CredentialView>;

export const CreateCredentialRequest = z.object({
  name: z.string().regex(/^[a-z0-9][a-z0-9._-]{1,62}$/),
  kind: CredentialKind,
  description: z.string().max(500).default(''),
  value: z.string().min(1).max(8192),
});
export const RotateCredentialRequest = z.object({ value: z.string().min(1).max(8192) });
/** Editable metadata of a credential (the value changes only through rotation). */
export const UpdateCredentialRequest = z.object({ description: z.string().max(500) });

export const CredentialVerifyResult = z.object({
  credentialId: z.string().uuid(),
  version: z.number().int(),
  decryptable: z.boolean(),
  fingerprintMatches: z.boolean(),
});
export type CredentialVerifyResult = z.infer<typeof CredentialVerifyResult>;
